<template>
	<div :class="VALIDATION_ERROR ? 'has-error' : ''" v-bind="c_test_attrs">
		<template v-if="emptyValueDisplay.active">
			<div :style="{ color: emptyValueDisplay.color }" class="form-control-static">{{ emptyValueDisplay.text }}</div>
		</template>
		<template v-else>
			<template v-if="field_meta.ctrlParams.isTextarea">
				<textarea
					class="form-control d-input"
					v-model="c_value"
					ref="inputRef"
					test-id="input"
					@change="handleChange"
					@input="mixin_inputEvent"
					:disabled="is_disabled"
					:readonly="c_ctrlParams.isReadOnly"
				/>
			</template>
			<template v-if="!field_meta.ctrlParams.isTextarea">
				<input
					class="form-control d-input"
					v-model="c_value"
					ref="inputRef"
					test-id="input"
					@change="handleChange"
					@input="mixin_inputEvent"
					:disabled="is_disabled || field_meta.isSearchEmpty"
					:readonly="c_ctrlParams.isReadOnly"
				/>
			</template>
			<template v-if="field_meta.ctrlParams.searchEmpty">
				<div>
					<label class="d-checkbox">
						<input type="checkbox" v-model="field_meta.isSearchEmpty" class="d-checkbox__mark" />
						<span class="d-checkbox__label">Искать пустые значения</span>
					</label>
				</div>
			</template>
			<div class="help-block">{{ VALIDATION_ERROR_TEXT() }}</div>
			<div class="ctrl-extraDescr" v-if="c_ctrlParams.extraDescr" v-html="c_ctrlParams.extraDescr"></div>
		</template>
	</div>
</template>

<script>
import axios from 'axios';
import Inputmask from 'inputmask';
import ctrlMixins from './_mixins.js';
import { ctrlParamsForAll, ctrlProps4All } from './_common-stuff.ts';
import { getEmptyValueDisplay } from './../utils.js';
import validationStuff from './../validationStuff.js';
import urlUtil from '../util-url.js';

export default {
	name: 'ctrl_text_input',
	meta: {
		// это наши данные, т.е. вуй при объявлении компонента их не использует
		title: 'Текстовое поле',
		params: {
			isTextarea: {
				name: 'Textarea',
				editType: 'checkbox',
			},
			defaultValue: {
				name: 'Значение по умолчанию',
				editType: 'input',
				tip: 'Применяется только в карточке и только при создании записи',
			},
			strictSearch: {
				name: 'Строгий поиск',
				editType: 'checkbox',
				tip: 'Рекомендуется для поля «Логин» в фильтре. <br/>С этим параметром при фильтрации оператор в условии будет не «ILIKE», а «=». <br/>То есть искать надо будет строку целиком и в том же регистре, в каком она хранится в БД.',
			},
			disableTextProcessing: {
				name: 'Отключение преобразований текста',
				editType: 'checkbox',
				tip: 'Текст вставляется "как есть", отключены все преобразования, например кодировка доменов в условные выражения с решётками, выкусывание определенных тегов и атрибутов с целью защиты от XSS, выкусывание невидимых символов которые ломают XML и так далее',
			},
			validationRules: {
				name: 'Валидация',
				editType: 'validationRulesEditor_text',
			},
			isReadOnly: {
				name: 'Нередактируемое',
				editType: 'checkbox',
			},
			searchEmpty: {
				name: 'Искать пустые',
				editType: 'checkbox',
			},
			emptyValueDisplayProps: {
				name: 'Кастомный показ пустых значений',
				tip: 'Если проставить флаг "вкл", то поле с пустым значением будет либо скрыто целиком, либо, если указан текст-плейсхолдер, вместо пустой строки будет выведен этот текст',
				editType: 'emptyValueDisplayPropsEd',
			},
			groupUpdateData: {
				name: 'Пакетное обновление данных',
				editType: 'checkbox',
			},
			encryptionFlag: {
				name: 'Флаг шифрования',
				editType: 'input',
				tip: 'Применяется только в карточке для указания флага, отвечающего за управление шифрованием значения поля с помощью сервиса Encoder.',
			},
			...ctrlParamsForAll,
		},
	},
	mixins: [
		ctrlMixins, // c_value, c_ctrlParams, c_srv_options, пропсы view_type, field_meta и так далее
		validationStuff.mixin,
	],
	props: {
		// большая часть пропсов приезжает из ctrlMixins!
		value: {
			default: '',
		},
		card_meta: ctrlProps4All.card_meta,
	},
	data() {
		return {
			_encryptionValue: undefined,
			_encryptionCanceler: undefined,
			emptyValueDisplay: getEmptyValueDisplay(
				this.$props.is_new_record,
				this.$props.field_meta.ctrlParams,
				this.$props.value,
			),
		};
	},
	computed: {
		canEncrypt() {
			return Boolean(this.c_ctrlParams.encryptionFlag);
		},
		isEncrypted() {
			if (!this.canEncrypt) return false;

			const field = this.card_meta?.find(
				(field) => field.ctrlCode === 'flag' && field.dbcolname === this.field_meta.ctrlParams.encryptionFlag,
			);

			if (!field) {
				console.error(`Флаг ${this.c_ctrlParams.encryptionFlag} для управления шифрованием не найден.`);

				return false;
			}

			return field.value ?? false;
		},
	},
	methods: {
		async fetchEncryption(value, isEncrypted) {
			if (!value?.length) return { encoded: '', decoded: '' };

			if (this._encryptionCanceler) {
				this._encryptionCanceler('Предыдущий запрос шифрования отменён');
			}

			const apiUrl = `${urlUtil.getDomain()}/${isEncrypted ? 'encode' : 'decode'}`;
			const params = isEncrypted ? { decoded_string: this.c_value } : { encoded_string: this.c_value };
			const cancelToken = new axios.CancelToken((c) => (this._encryptionCanceler = c));

			const response = await axios.post(apiUrl, undefined, { params, cancelToken });
			const data = response.data;

			if (data.status === 'OK') {
				return { encoded: data.encodedString, decoded: data.decodedString };
			}

			throw new Error(data?.errorMsg ?? 'Ответ не содержит зашифрованное значение');
		},
		async handleChange() {
			this._encryptionValue = undefined;

			if (!this.canEncrypt || !this.c_value?.length) {
				this.mixin_saveEvent();

				return;
			}

			try {
				const encryptionValue = await this.fetchEncryption(this.c_value, true);
				this._encryptionValue = encryptionValue;
				if (encryptionValue) {
					if (this.isEncrypted) {
						this.$notify({ type: 'success', text: 'Значение успешно зашифровано.' });
						this.c_value = this._encryptionValue.encoded;
					} else {
						this.c_value = this._encryptionValue.decoded;
					}
				}
			} catch (error) {
				if (error?.name === 'CanceledError') return;

				console.error(error);
				if (this.isEncrypted) {
					this.$notify({ type: 'error', text: 'Не удалось зашифровать значение.' });
				}
			} finally {
				this.mixin_saveEvent();
			}
		},
	},
	mounted() {
		if (this.field_meta.ctrlParams && this.field_meta.ctrlParams.validationRules) {
			let rules = this.field_meta.ctrlParams.validationRules;
			if (rules[Object.keys(rules)[0]].mask) {
				const imask = new Inputmask(rules[Object.keys(rules)[0]].mask);
				imask.mask(this.$refs.inputRef);

				this.$nextTick(() => {
					const value = this.$refs.inputRef.value;

					if (value) {
						// this.$emit('update:value', value);
						let event = new Event('input');
						this.$refs.inputRef.dispatchEvent(event);
					}
				});
			}
		}

		if (this.canEncrypt) {
			this.fetchEncryption(this.c_value, !this.isEncrypted)
				.then((encryptionValue) => {
					this._encryptionValue = encryptionValue;
				})
				.catch(() => {
					this._encryptionValue = this.isEncrypted
						? { encoded: this.c_value, decoded: '' }
						: { encoded: '', decoded: this.c_value };
				});
		}
	},
	watch: {
		isEncrypted(isEncrypted, oldIsEncrypted) {
			if (!this.canEncrypt || isEncrypted === oldIsEncrypted) return;

			if (isEncrypted && this.c_value?.length) {
				this.$notify({
					type: this._encryptionValue ? 'success' : 'error',
					text: this._encryptionValue ? 'Значение успешно зашифровано.' : 'Не удалось зашифровать значение.',
				});
			}

			if (this._encryptionValue) {
				this.c_value = isEncrypted ? this._encryptionValue.encoded : this._encryptionValue.decoded;
			}
		},
	},
};
</script>

<style>
textarea.form-control {
	/* PIRS2018-1846 */
	resize: both;
	margin: 0 !important;
}

.resize-polyfill-polyfilled textarea {
	height: 100% !important;
}
</style>
